// utils/authApi.js
import { auth } from '../firebase/firebaseConfig';

/**
 * Get the current user's Firebase ID token
 * @returns {Promise<string>} Firebase ID token
 * @throws {Error} If user is not authenticated
 */
export const getAuthToken = async () => {
    const user = auth.currentUser;

    if (!user) {
        throw new Error('User is not authenticated');
    }

    try {
        // Get fresh token (forces refresh if expired)
        const token = await user.getIdToken(true);
        return token;
    } catch (error) {
        console.error('Error getting auth token:', error);
        throw new Error('Failed to get authentication token');
    }
};

/**
 * Make an authenticated fetch request to the backend
 * @param {string} url - API endpoint URL
 * @param {object} options - Fetch options (method, body, headers, etc.)
 * @returns {Promise<Response>} Fetch response
 */
export const authenticatedFetch = async (url, options = {}) => {
    try {
        // Get Firebase ID token
        const token = await getAuthToken();

        // Merge authorization header with existing headers
        const headers = {
            ...options.headers,
            'Authorization': `Bearer ${token}`
        };

        // Make the fetch request with auth header
        const response = await fetch(url, {
            ...options,
            headers
        });

        // Handle 401 Unauthorized errors
        if (response.status === 401) {
            console.error('❌ Authentication failed - 401 Unauthorized');
            throw new Error('Authentication failed. Please sign in again.');
        }

        return response;
    } catch (error) {
        // Re-throw authentication errors
        if (error.message.includes('not authenticated') || error.message.includes('Authentication failed')) {
            throw error;
        }

        // Handle network errors
        console.error('API request error:', error);
        throw new Error(`API request failed: ${error.message}`);
    }
};

/**
 * Make an unauthenticated fetch request to the backend
 * Use this for public endpoints like /check-admin-auth
 * @param {string} url - API endpoint URL
 * @param {object} options - Fetch options
 * @returns {Promise<Response>} Fetch response
 */
export const publicFetch = async (url, options = {}) => {
    return fetch(url, options);
};
